import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { type TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { SharedContextProps } from "@lib/types";
import { XAICallDrawer } from "@lib/components/PageComponents/XAICall";
import { useQuery } from "@tanstack/react-query";
import { apiGetXAICalls } from "@lib/api/xAI";
import { XAICall } from "@lib/types/xAI";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { IoMdClose } from "react-icons/io";
import { TbListDetails } from "react-icons/tb";
import TftSalesCallLogs from "./tftSalesCallLogs";
import { useNavigate } from "react-router-dom";

const SharedTftSalesCalls: React.FC<SharedContextProps> = ({
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight = 0,
  tableProps,
  height,
  queryParams,
  detailContext,
  customerId,
}) => {
  const navigate = useNavigate();
  const { messages } = useIntl();
  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<XAICall | null>(null);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: XAICall) => {
    setSelected(item);
    toggle(true);
  };

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer", "lead"],
    filter: initialFilters,
    ...queryParams,
  });

  const callsQuery = useQuery({
    queryKey: ["tft-sales-calls", JSON.stringify(params)],
    queryFn: () => apiGetXAICalls(params),
    enabled: !!params && !!pageIsActive,
  });

  const { data, isLoading, isFetching } = callsQuery;
  const apiData = data?.data?.data;

  const { data: biggestDurationData } = useQuery({
    queryKey: ["tft-sales-calls-biggest-duration"],
    queryFn: () =>
      apiGetXAICalls({
        sort: "durationMs,DESC",
        limit: 1,
      }),
    enabled: !!apiData,
  });
  const biggestDuration = Number(
    biggestDurationData?.data?.data?.[0]?.durationMs || 0,
  );

  const { data: biggestCostData } = useQuery({
    queryKey: ["tft-sales-calls-biggest-cost"],
    queryFn: () =>
      apiGetXAICalls({
        sort: "callCost,DESC",
        limit: 1,
      }),
    enabled: !!apiData,
  });
  const biggestCost = Number(biggestCostData?.data?.data?.[0]?.callCost || 0);

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<XAICall>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        navigate,
        apiData,
        params,
        messages,
        handleSelect,
        columnsConfig: tableProps?.columnsConfig,
        biggestDuration,
        biggestCost,
        detailContext,
      }),
    [
      apiData,
      params,
      tableProps?.columnsConfig,
      messages,
      handleSelect,
      detailContext,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params?.filter, columns, hiddenFilters]);

  const title = customTitle || (messages["sidebar.tftSalesCalls"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      minusHeight={minusHeight}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={callsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={{ onChange, columns, ...tableProps }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          expandable={{
            // showExpandColumn: false,
            columnTitle: "#",
            columnWidth: "50px",
            fixed: "right",
            expandIcon: ({ expanded, record, onExpand }) => (
              <ActionButton
                type={expanded ? "close" : "detail"}
                icon={expanded ? <IoMdClose /> : <TbListDetails />}
                color={expanded ? "red" : "green"}
                onClick={(e) => onExpand(record, e)}
                noLabel
              />
            ),
            expandedRowRender: (record) => <TftSalesCallLogs item={record} />,
            onExpand(expanded, record) {
              try {
                const find = document.querySelector(
                  `[data-row-key="${record.id}"]`,
                );
                if (find) {
                  find.setAttribute(
                    "data-row-expanded",
                    expanded ? "true" : "false",
                  );
                }
              } catch (error) {}
            },
          }}
          {...tableProps}
        />
      </AppsContent>

      <XAICallDrawer
        open={openModal}
        onClose={() => toggle(false)}
        item={selected}
      />
    </AppsContainer>
  );
};

export default SharedTftSalesCalls;
